import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Grid,
    Paper,
    Chip,
    Button,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Card,
    CardMedia,
    useTheme,
    useMediaQuery,
    Stack,
    CircularProgress,
    Tooltip,
    Snackbar,
    Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import WebIcon from '@mui/icons-material/Web';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import LaunchIcon from '@mui/icons-material/Launch';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { apps } from '../../data/appData';
import WebAppEmbed from '../../components/WebAppEmbed/WebAppEmbed';
import './AppDetails.scss';

const AppDetails: React.FC = () => {
    const { appId } = useParams<{ appId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showWebApp, setShowWebApp] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const [redirectMessage, setRedirectMessage] = useState('');
    const [showCopyAlert, setShowCopyAlert] = useState(false);
    const [countdown, setCountdown] = useState(5);
    const [copied, setCopied] = useState(false);

    // Find the app data based on the ID
    const app = apps.find((app) => app.id === appId);

    // Function to copy link to clipboard - define this early to avoid reference errors
    const copyLinkToClipboard = (id: string) => {
        if (!id) return;

        const url = `https://www.planetbinc.com/app/${id}?redirect=true`;
        navigator.clipboard.writeText(url)
            .then(() => {
                setCopied(true);
                setShowCopyAlert(true);
                console.log('URL copied to clipboard');
            })
            .catch(err => {
                console.error('Failed to copy URL: ', err);
            });
    };

    // Reset copied state after a delay
    useEffect(() => {
        if (copied) {
            const timer = setTimeout(() => {
                setCopied(false);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [copied]);

    // Countdown effect for redirects
    useEffect(() => {
        if (isRedirecting && countdown > 0 && !redirectMessage.includes('mobile device')) {
            const timer = setTimeout(() => {
                setCountdown(countdown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [isRedirecting, countdown, redirectMessage]);

    useEffect(() => {
        // Parse query parameters from the URL
        const queryParams = new URLSearchParams(location.search);
        const redirectParam = queryParams.get('redirect');
        // More lenient check for redirect parameter
        const shouldRedirect = redirectParam === 'true' || redirectParam === '';  // Empty value is also considered true for ?redirect

        // Always log these parameters, even in production
        console.log('URL location:', window.location.href);
        console.log('URL params:', location.search);
        console.log('Redirect param:', redirectParam);
        console.log('Should redirect:', shouldRedirect);
        console.log('App exists:', !!app);

        // Reset redirect state if the param is removed
        if (!shouldRedirect) {
            if (isRedirecting) {
                setIsRedirecting(false);
            }
            return;
        }

        // Handle redirect logic
        if (app) {
            try {
                // Always set redirecting state to true when redirect param is true
                setIsRedirecting(true);
                setRedirectMessage('Detecting your device...');

                // Detect user agent to determine device type
                const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera || '';
                console.log('User agent:', userAgent);

                // Check for iOS devices
                const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream;

                // Check for Android devices
                const isAndroid = /android/i.test(userAgent);

                // Check for desktop browsers - simplified detection
                const isDesktop = !(/Mobile|Android|iPhone|iPad|iPod/i.test(userAgent));

                console.log('Device detection:', { isIOS, isAndroid, isDesktop });
                console.log('Setting redirecting state:', true);

                // Determine which store link to use
                let redirectUrl = '';

                if (isDesktop) {
                    // For desktop browsers, don't redirect but show instructions
                    setRedirectMessage('This app is designed for mobile devices. You can download it by scanning the QR code below or using the direct links.');
                    // Keep redirectUrl empty so no redirect happens
                    console.log('Desktop detected, showing QR code');
                } else if (isIOS && app.appStoreLink) {
                    redirectUrl = app.appStoreLink;
                    setRedirectMessage(`Redirecting to App Store in ${countdown} seconds...`);
                    setCountdown(5); // Reset countdown
                } else if (isAndroid && app.googlePlayLink) {
                    redirectUrl = app.googlePlayLink;
                    setRedirectMessage(`Redirecting to Google Play Store in ${countdown} seconds...`);
                    setCountdown(5); // Reset countdown
                } else if (app.webLink) {
                    // Use webLink for mobile browsers without dedicated app stores
                    redirectUrl = app.webLink;
                    setRedirectMessage(`Redirecting to Web App in ${countdown} seconds...`);
                    setCountdown(5); // Reset countdown
                } else if (app.appStoreLink) {
                    // Default to App Store only for mobile devices if no match but it exists
                    redirectUrl = app.appStoreLink;
                    setRedirectMessage(`Redirecting to App Store in ${countdown} seconds...`);
                    setCountdown(5); // Reset countdown
                } else if (app.googlePlayLink) {
                    // Or default to Google Play only for mobile devices if that exists
                    redirectUrl = app.googlePlayLink;
                    setRedirectMessage(`Redirecting to Google Play Store in ${countdown} seconds...`);
                    setCountdown(5); // Reset countdown
                }

                // Perform the redirect after a short delay
                if (redirectUrl) {
                    console.log('Redirect URL set:', redirectUrl);
                    setTimeout(() => {
                        try {
                            console.log('Attempting redirect to:', redirectUrl);
                            window.location.href = redirectUrl;
                        } catch (error) {
                            console.error('Redirect failed:', error);
                            setIsRedirecting(false);
                        }
                    }, 5000); // Changed from 1500 to 5000 (5 seconds)
                } else {
                    console.log('No redirect URL set. isDesktop:', isDesktop);
                    // Don't reset redirecting state for desktop browsers
                    if (!isDesktop) {
                        setIsRedirecting(false);
                        setRedirectMessage('No download link available for your device.');
                    }
                }
            } catch (error) {
                console.error('Error in redirect:', error);
                setIsRedirecting(false);
            }
        }
    }, [app, location.search]);

    // Debug render state
    console.log('Render state:', { isRedirecting, redirectMessage });

    // If app not found, show a message
    if (!app) {
        return (
            <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h4" gutterBottom>
                    App Not Found
                </Typography>
                <Typography variant="body1" gutterBottom>
                    The app you're looking for doesn't exist or has been removed.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/')}
                    sx={{ mt: 3 }}
                    startIcon={<ArrowBackIcon />}
                >
                    Back to Home
                </Button>
            </Container>
        );
    }

    // If redirecting, show a redirect screen
    if (isRedirecting) {
        // Check if the redirect message indicates we're on desktop
        const isDesktopMessage = redirectMessage.includes('mobile device');
        console.log('Showing redirect screen with desktop message?', isDesktopMessage);

        return (
            <Container maxWidth="sm" sx={{ py: 12, textAlign: 'center' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                    <img src={app.icon} alt={app.name} style={{ width: '120px', height: '120px' }} />
                    <Typography variant="h4" gutterBottom>
                        {app.name}
                    </Typography>

                    {!isDesktopMessage && (
                        <>
                            <CircularProgress color="primary" />
                            <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h2" color="primary.main" sx={{ fontWeight: 'bold', mx: 2 }}>
                                    {countdown}
                                </Typography>
                            </Box>
                        </>
                    )}

                    <Typography variant="h6" color="text.secondary">
                        {redirectMessage}
                    </Typography>

                    {isDesktopMessage ? (
                        <>
                            <Box sx={{ my: 3, p: 4, border: '2px solid', borderColor: 'primary.main', borderRadius: 2, bgcolor: 'background.paper', boxShadow: 3 }}>
                                <Typography variant="h6" color="primary" gutterBottom>
                                    Download {app.name} on your mobile device
                                </Typography>
                                <Typography variant="body1" gutterBottom>
                                    Scan this QR code with your mobile device camera:
                                </Typography>
                                <Box
                                    sx={{
                                        mt: 3,
                                        mb: 2,
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://www.planetbinc.com/app/${app.id}?redirect=true`}
                                        alt={`QR Code for ${app.name}`}
                                        width="200"
                                        height="200"
                                        style={{ border: '10px solid white' }}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => app && copyLinkToClipboard(app.id)}
                                        startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                                        sx={{ mb: 2 }}
                                    >
                                        {copied ? 'Link copied!' : 'Click here to copy link'}
                                    </Button>
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    Opening this link on a mobile device will automatically redirect you to the appropriate app store.
                                </Typography>
                            </Box>

                            <Typography variant="body1" color="primary.main" fontWeight="bold">
                                Or visit the app stores directly:
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            If you are not redirected automatically, please click below:
                        </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                        {app.appStoreLink && (
                            <Button
                                variant="contained"
                                color="primary"
                                href={app.appStoreLink}
                                target="_blank"
                                startIcon={<AppleIcon />}
                            >
                                App Store
                            </Button>
                        )}
                        {app.googlePlayLink && (
                            <Button
                                variant="contained"
                                color="primary"
                                href={app.googlePlayLink}
                                target="_blank"
                                startIcon={<AndroidIcon />}
                            >
                                Google Play
                            </Button>
                        )}
                        {app.webLink && (
                            <Button
                                variant="contained"
                                color="secondary"
                                href={app.webLink}
                                target="_blank"
                                startIcon={<WebIcon />}
                            >
                                Web App
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                // Cancel redirect and show regular page
                                setIsRedirecting(false);
                            }}
                        >
                            {isDesktopMessage ? 'View App Details' : 'Cancel Redirect'}
                        </Button>
                    </Box>
                </Box>
            </Container>
        );
    }

    const toggleWebApp = () => {
        setShowWebApp(!showWebApp);
    };

    // For testing - add a button at the top of the normal page view to force show redirect
    const forceShowRedirect = () => {
        setIsRedirecting(true);
        setRedirectMessage('This app is designed for mobile devices. You can download it by scanning the QR code below or using the direct links.');
    };

    return (
        <div className="app-details-page">
            {/* Test button - only in development */}
            {process.env.NODE_ENV !== 'production' && (
                <Box sx={{ position: 'fixed', top: 10, right: 10, zIndex: 9999 }}>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={forceShowRedirect}
                        sx={{ mb: 2 }}
                    >
                        Test Redirect Screen
                    </Button>
                </Box>
            )}

            <Box className="app-header" sx={{
                backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                color: 'white',
                py: 8
            }}>
                <Container maxWidth="lg">
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={() => navigate('/')}
                        startIcon={<ArrowBackIcon />}
                        sx={{ mb: 4 }}
                    >
                        Back to Apps
                    </Button>

                    <Grid container spacing={4} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <Box className="app-icon-wrapper">
                                <img src={app.icon} alt={app.name} className="app-icon" />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Typography variant="h3" component="h1" gutterBottom>
                                {app.name}
                            </Typography>
                            <Typography variant="h6" paragraph>
                                {app.description}
                            </Typography>

                            {/* <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                                {app.status.map((status, index) => (
                                    <Chip
                                        key={index}
                                        label={`${status.platform} ${status.version} - ${status.status}`}
                                        color={status.status === 'Ready for Distribution' ? 'success' : 'warning'}
                                        icon={status.platform === 'iOS' ? <AppleIcon /> : <AndroidIcon />}
                                    />
                                ))}
                            </Box> */}

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                {app.appStoreLink && (
                                    <a
                                        href={app.appStoreLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="store-badge-link"
                                    >
                                        <img
                                            src="/images/app-store-badge.svg"
                                            alt="Download on the App Store"
                                            width={isMobile ? 120 : 140}
                                            height={40}
                                        />
                                    </a>
                                )}

                                {app.googlePlayLink && (
                                    <a
                                        href={app.googlePlayLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="store-badge-link"
                                    >
                                        <img
                                            src="/images/google-play-badge.svg"
                                            alt="Get it on Google Play"
                                            width={isMobile ? 120 : 140}
                                            height={40}
                                        />
                                    </a>
                                )}

                                {app.webLink && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<LaunchIcon />}
                                        onClick={toggleWebApp}
                                        sx={{
                                            height: 40,
                                            borderRadius: '8px',
                                            bgcolor: 'background.paper',
                                            borderWidth: '2px',
                                            '&:hover': {
                                                borderWidth: '2px',
                                                transform: 'translateY(-3px)',
                                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                                                transition: 'all 0.3s ease'
                                            }
                                        }}
                                    >
                                        {showWebApp ? 'Hide Web App' : 'Use Web App'}
                                    </Button>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: 8 }}>
                {app.webLink && showWebApp && (
                    <>
                        <Typography variant="h4" gutterBottom>
                            Try Web App
                        </Typography>
                        <Divider sx={{ mb: 3 }} />
                        <WebAppEmbed
                            title={`${app.name} Web App`}
                            url={app.webLink}
                            onClose={toggleWebApp}
                        />
                    </>
                )}

                <Grid container spacing={6}>
                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" gutterBottom>
                            Features
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Paper elevation={2} sx={{ p: 3 }}>
                            <List>
                                {app.features.map((feature, index) => (
                                    <ListItem key={index}>
                                        <ListItemIcon>
                                            <CheckCircleOutlineIcon color="primary" />
                                        </ListItemIcon>
                                        <ListItemText primary={feature} />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h4" gutterBottom>
                            Screenshots
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={2}>
                            {app.screenshots.map((screenshot, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <Card>
                                        <CardMedia
                                            component="img"
                                            image={screenshot}
                                            alt={`${app.name} Screenshot ${index + 1}`}
                                            className="app-screenshot"
                                        />
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>

                {/* Mobile Download Section */}
                <Box className="mobile-download-section">
                    <Typography variant="h6" component="h3" gutterBottom>
                        Download {app.name}
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={7}>
                            <Typography variant="body1" gutterBottom>
                                Get {app.name} on your mobile device
                            </Typography>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                sx={{ mt: 2 }}
                            >
                                {app.appStoreLink && (
                                    <a
                                        href={app.appStoreLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="store-badge-link"
                                    >
                                        <img
                                            src="/images/app-store-badge.svg"
                                            alt="Download on App Store"
                                            className="store-badge"
                                        />
                                    </a>
                                )}

                                {app.googlePlayLink && (
                                    <a
                                        href={app.googlePlayLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="store-badge-link"
                                    >
                                        <img
                                            src="/images/google-play-badge.svg"
                                            alt="Get it on Google Play"
                                            className="store-badge"
                                        />
                                    </a>
                                )}

                                {app.webLink && (
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<WebIcon />}
                                        onClick={toggleWebApp}
                                        className="web-app-button"
                                    >
                                        {showWebApp ? 'Hide Web App' : 'Try Web Version'}
                                    </Button>
                                )}
                            </Stack>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <Box className="qr-code-container">
                                <Box className="qr-code">
                                    <img
                                        src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=https://www.planetbinc.com/app/${app.id}?redirect=true`}
                                        alt={`QR Code for ${app.name}`}
                                        width="100%"
                                        height="auto"
                                    />
                                </Box>
                                <Typography variant="caption" align="center" sx={{ mt: 1, display: 'block' }}>
                                    Scan to download on any device
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
                                    <Button
                                        variant="text"
                                        color="primary"
                                        size="small"
                                        onClick={() => app && copyLinkToClipboard(app.id)}
                                        startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
                                    >
                                        {copied ? 'Link copied!' : 'Click here to copy link'}
                                    </Button>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Container>
        </div>
    );
};

export default AppDetails; 